import { TIER_LABELS } from '../../core/index.js'
import type { ScoreResult } from './simulatorModel.js'

function Lamp({ label, isLit, tone }: { label: string; isLit: boolean; tone: 'coral' | 'green' }) {
  const lit = tone === 'coral' ? 'border-coral bg-coral/20 text-coral shadow-[0_0_8px_rgba(242,107,91,0.6)]' : 'border-green bg-green/20 text-green shadow-[0_0_8px_rgba(46,139,87,0.6)]'
  return <span className={`rounded-sm border px-2 py-1 text-[10px] font-black tracking-widest ${isLit ? lit : 'border-white/15 text-white/25'}`}>{label}</span>
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[9px] font-bold uppercase tracking-widest text-white/45">{label}</div>
      <div className="font-mono text-sm font-bold text-white">{value}</div>
    </div>
  )
}

const signed = (points: number): string => `${points >= 0 ? '+' : '−'}${Math.abs(points).toFixed(1)}`

interface ScoreReadoutProps {
  /** The latest calculation; undefined before the first one returns. */
  result: ScoreResult | undefined
  /** True while the faders have moved on from what `result` was calculated for. */
  isStale: boolean
  error: string | null
}

/** The desk's output: the score in lights, the tier it lands in, and every step between the weighted average and the final number. */
export function ScoreReadout({ result, isStale, error }: ScoreReadoutProps) {
  const score = result?.score
  return (
    <div className="rounded-lg border border-zinc-700 bg-zinc-950 p-4 shadow-md">
      <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
        <div>
          <div className="text-[9px] font-bold uppercase tracking-widest text-white/45">Score</div>
          <div aria-live="polite" aria-atomic="true" className={`font-mono text-6xl font-black tabular-nums leading-none text-coral transition-opacity ${isStale ? 'opacity-50' : ''}`} style={{ textShadow: '0 0 18px rgba(242,107,91,0.55)' }}>
            {score === undefined ? '--.-' : score === null ? 'NONE' : score.toFixed(1)}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="text-lg font-black uppercase tracking-wide text-white">{result?.tier ? `${TIER_LABELS[result.tier]} tier` : 'No tier'}</div>
          <div className="flex gap-2">
            <Lamp label="PROVISIONAL" isLit={result?.provisional ?? false} tone="coral" />
            <Lamp label="TOP TIER" isLit={result?.tier === 'gold'} tone="green" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
          <Stat label="Weighted" value={result?.weightedScore == null ? '—' : result.weightedScore.toFixed(2)} />
          <Stat label="Next tier" value={result?.nextTier ? `${TIER_LABELS[result.nextTier.tier]} in ${result.nextTier.pointsNeeded.toFixed(1)}` : '—'} />
          <Stat label="Next clean shift" value={result?.nextCleanShiftGain == null ? '—' : signed(result.nextCleanShiftGain)} />
          <Stat label="Weight counted" value={result ? `${result.includedWeight}` : '—'} />
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-3 text-xs font-bold text-coral">
          {error}
        </p>
      )}
      {result?.score === null && <p className="mt-3 text-xs text-white/60">Nothing can be scored: every behavior is off, has no weight, or has no data in this time frame.</p>}
      {result && result.adjustments.length > 0 && (
        <ul className="mt-3 space-y-1 border-t border-white/10 pt-3 font-mono text-[11px] text-white/70">
          {result.adjustments.map((adjustment) => (
            <li key={adjustment.kind} className="flex justify-between gap-4">
              <span>{adjustment.description}</span>
              <span className="font-bold text-white">{signed(adjustment.points)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
